import { Injectable } from '@angular/core';
import { combineLatest, Observable} from 'rxjs';
import {
  Registration, Invoice, InvoiceOrganization,
  InvoicePlace, Member, InvoiceLine, InvoicePrice,
  InvoiceAmount, CourseInstance, Organization, Building,
} from '../../models';
import { map, switchMap } from 'rxjs/operators';
import { BuildingService, CourseInstanceService, MemberService } from '../../data';
import { InvoicingService } from './invoicing.service';
import { ServiceBookingInvoiceService, ServiceInvoiceData } from './service-booking-invoice.service';
import { Presential } from '../../../@academy/catalog/models/Presential.model';
import { SortingService } from '../sorting.service';
import { DatePipe } from '@angular/common';

/**
 * Service containing tools for converting the data of a CourseInstance and Registrations
 * into information relevant to invoicing
 */
@Injectable()
export class RegistrationInvoiceService extends InvoicingService {

  private registrationInvoice: Invoice = new Invoice();

  constructor(
    private buildingService: BuildingService,
    private courseInstanceService: CourseInstanceService,
    private memberService: MemberService,
    private serviceBookingService: ServiceBookingInvoiceService,
    private sortingService: SortingService,
    private datePipe: DatePipe,
  ) {
    super();
  }

  getCourseInstanceLine(courseInstance: CourseInstance): InvoiceLine {
    courseInstance.scheduleActions.sort(this.sortingService.orderBy('startTime'));

    const nbrHalfDays = courseInstance.scheduleActions.length;
    const formatStartDate = this.datePipe.transform(courseInstance.scheduleActions[0].startTime, 'short');
    const formatEndDate = this.datePipe.transform(courseInstance.scheduleActions[nbrHalfDays - 1].endTime, 'short');
    const itemDescription = `${(courseInstance.course as Presential).name} | ${formatStartDate} - ${formatEndDate}`;

    return new InvoiceLine({
      itemDescription,
      billingObject: 'TRAINING',
      price: new InvoicePrice({
        quantity: 1,
        vatCode: 1,
        invoiceAmount: new InvoiceAmount({
          currency: courseInstance.currency,
          unitAmount: courseInstance.price,
        }),
      }),
    });
  }

  getInvoice(registration: Registration): Observable<Invoice> {
    return combineLatest([
      this.setInvoiceSupplier(registration),
      this.setInvoiceCustomer(registration),
      this.setInvoiceLines(registration),
    ]).pipe(map(([
                   supplier,
                   customer,
                   lines,
                 ]) => {
      this.registrationInvoice.header = super.getInvoiceHeader();
      this.registrationInvoice.supplier = supplier.supplier;
      this.registrationInvoice.supplierEco = supplier.supplierEco;
      this.registrationInvoice.customer = customer.customer;
      this.registrationInvoice.customerEco = customer.customerEco;
      this.registrationInvoice.lines = lines;
      return this.registrationInvoice;
    }));
  }

  /**
   * Set and return both customer and customerEco according to the given room booking.
   * Set also the public roomBookingInvoice headers with customer's informations.
   *
   * @param registration
   * @return
   */
  setInvoiceCustomer(registration): Observable<{ customer?: InvoiceOrganization; customerEco?: InvoicePlace }> {
    const agentId = registration.agent.split('/').pop();
    return this.memberService.get(agentId).pipe(
      switchMap(owner => {
        owner = new Member(owner);
        this.registrationInvoice.header.customerEmail = owner.getEmail();
        this.registrationInvoice.header.customerContact = owner.getFullName();
        return super.getInvoiceableNumber(owner);
      }),
      map(invoiceableNumber => ({
          customer: invoiceableNumber instanceof InvoiceOrganization ? invoiceableNumber : null,
          customerEco: invoiceableNumber instanceof InvoicePlace ? invoiceableNumber : null,
        })));
  }

  setInvoiceLines(registration: Registration) {
    const courseInstanceId = (registration.object as string).split('/').pop();

    return combineLatest([
      this.courseInstanceService.get(courseInstanceId),
      this.courseInstanceService.getCourseInstanceServiceBookings(courseInstanceId)],
    )
      .pipe(map(([courseInstance, courseInstanceServiceBookings]) => {

        const enhancedServiceBookings = this.serviceBookingService
          .enhanceServiceBookingsService(courseInstanceServiceBookings['hydra:member']);
        const serviceInvoiceData: ServiceInvoiceData[] = this.serviceBookingService
          .getServiceInvoiceData(enhancedServiceBookings);
        const servicesLines: InvoiceLine[] = this.serviceBookingService
          .getServiceBookingsLines(serviceInvoiceData, courseInstance.currency, false);

        const courseInstanceLine = this.getCourseInstanceLine(courseInstance);

        return [courseInstanceLine, ...servicesLines];
      }));
  }

  /**
   * Returns both supplier and supplierEco
   *
   * @param registration
   *
   * @return
   */
  setInvoiceSupplier(registration: any): Observable<{ supplier?: InvoiceOrganization; supplierEco?: InvoicePlace }> {
    const courseInstanceId = registration['object'].split('/').pop();
    return this.courseInstanceService.get(courseInstanceId).pipe(
      switchMap(courseInstance => {
        const buildingId = courseInstance['invoiceBuilding'].split('/').pop();
        return this.buildingService.get(buildingId);
      }),
      map((building: Building) => {
        const invoiceableNumber = (building.manager as Organization).costCenter;
        return {
          supplierEco: new InvoicePlace({costCenter: invoiceableNumber}),
          supplier: null,
        };
      }),
    );
  }
}
